import { Injectable } from "@angular/core";
import {
  RoomId,
  RoomMixerStreamEntity,
  RoomMixerStreamEntityRaw,
  ROOM_LIVE_STATUS
} from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType
} from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { apiErrorHttpStatus } from "@youm-front/api/data-access";
import { RemoteConferenceStreamActions } from "@youm-front/conference-stream/data-access";
import { LokiActions } from "@youm-front/loki/data-access";
import { defer, merge, Observable, of, throwError, timer } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  retry,
  switchMap,
  takeUntil
} from "rxjs/operators";
import { RoomMixerStreamActions } from "../actions";
import { RoomMixerStreamGetRequestDTO } from "../dto";
import { RoomLiveStreamStatusEvents, RoomMixerEvents } from "../events";
import { RoomContextFacade } from "../facades";
import { RoomMixerSelectors } from "../selectors";
import { RoomMixerService } from "../services";

// STARTED (and the mixer started event) is sent once the stream can be
// subscribed (YOUM-154): a missing stream then is a server contract
// violation, reported and logged, not polled.
const STREAM_MISSING = new Error("Room mixer stream missing after started");

// Internal error: the only API answer a retry may change. Any other API
// answer (unauthorized, forbidden, not found, also an unauthorized room
// YOUM-270, invalid input, ...) is reported at once.
const INTERNAL_ERROR_CODE = 1500;

// HTTP statuses of a failure in front of the API (no API code in the body)
// that a retry may change: no answer (0), timeout, rate limit, server or
// proxy error. Any other (a proxy 403, a 404 page, ...) is reported at once.
const TRANSIENT_HTTP_STATUSES: readonly number[] = [0, 408, 429];

// The live status that ends the stream of the room
function endsStream(status: string) {
  return status === ROOM_LIVE_STATUS.STOP || status === ROOM_LIVE_STATUS.ERROR;
}

// Spreads the retries of the viewers that got the same started event.
function jitter(ms: number) {
  const [random = 0] = crypto.getRandomValues(new Uint32Array(1));
  return ms * (0.8 + (random / 2 ** 32) * 0.4);
}

function errorCode(error: unknown) {
  const code = (error as null | { code?: unknown })?.code;
  return typeof code === "number" ? code : undefined;
}

// An API internal error, or a failure in front of the API that a retry may
// change. A text body records no HTTP status: unknown, so retried.
function isTransient(error: unknown) {
  if (error === STREAM_MISSING) {
    return false;
  }

  const code = errorCode(error);
  if (code !== undefined) {
    return code === INTERNAL_ERROR_CODE;
  }

  const status = apiErrorHttpStatus(error);
  return (
    status === undefined ||
    status >= 500 ||
    TRANSIENT_HTTP_STATUSES.includes(status)
  );
}

@Injectable()
export class RoomMixerStreamEffects {
  // Delay before each retry of a transient failure; its length bounds the
  // retries.
  static readonly RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];

  // The viewer will receive an event about a started mixer during the room join process.
  // No need for a dedicated get request.

  roomMixerStarted$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(RoomMixerEvents.roomMixerStartedEvent),
        map((_) => _.roomMixerStartedEventVO.value.roomId)
      ),
      this.actions$.pipe(
        ofType(RoomLiveStreamStatusEvents.update),
        filter((_) => _.status === ROOM_LIVE_STATUS.STARTED),
        map((_) => _.roomId)
      )
    ).pipe(
      map((_) =>
        RoomMixerStreamActions.roomMixerStreamGet({
          roomId: _
        })
      )
    )
  );

  roomMixerStop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomLiveStreamStatusEvents.update),
      // ERROR: the WebRTC stream of the live could not be produced (again)
      filter((_) => endsStream(_.status)),
      concatLatestFrom(() => this.store.select(RoomMixerSelectors.stream)),
      // Delete stream for the same room only
      filter(([_, stream]) => !!stream && _.roomId === stream.value.roomId),
      mergeMap(() => [RoomMixerStreamActions.dispose()])
    )
  );

  roomMixerDispose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomMixerStreamActions.dispose),
      concatLatestFrom(() => this.store.select(RoomMixerSelectors.stream)),
      mergeMap(([, stream]) =>
        stream
          ? [
              RoomMixerStreamActions.reset(),
              RemoteConferenceStreamActions.streamDelete({
                id: stream.value.id
              })
            ]
          : [RoomMixerStreamActions.reset()]
      )
    )
  );

  roomMixerStreamGet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomMixerStreamActions.roomMixerStreamGet),
      map((_) => _.roomId),
      switchMap((_) =>
        this.roomMixerStreamGetWithRetry(_).pipe(
          takeUntil(this.roomMixerStreamGetCancel(_))
        )
      )
    )
  );

  roomMixerStreamGetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomMixerStreamActions.roomMixerStreamGetSuccess),
      mergeMap((_) => (_.stream ? [_.stream] : [])),
      mergeMap((_) => [
        RemoteConferenceStreamActions.streamAdd({
          id: _.value.id,
          kinds: ["audio", "video"],
          credentials: _.value.credentials,
          roomContextId: this.roomContextFacade.roomContextId
        })
      ])
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly roomMixerService: RoomMixerService,
    private readonly store: Store,
    private readonly roomContextFacade: RoomContextFacade
  ) {}

  // A transient failure is retried a few times. A missing stream is not
  // retried: the next STARTED gets the stream again.
  private roomMixerStreamGetWithRetry(roomId: RoomId) {
    const delaysMs = RoomMixerStreamEffects.RETRY_DELAYS_MS;

    // Each retry sends a new request
    return defer(() =>
      this.roomMixerService.roomMixerStreamGet(
        new RoomMixerStreamGetRequestDTO({ roomId })
      )
    ).pipe(
      map((_) => _.value.stream),
      mergeMap((_) => (_ ? of(_) : throwError(() => STREAM_MISSING))),
      retry({
        count: delaysMs.length,
        delay: (error, retryCount) =>
          isTransient(error)
            ? timer(jitter(delaysMs[retryCount - 1] ?? 0))
            : throwError(() => error)
      }),
      map((_: RoomMixerStreamEntityRaw) =>
        RoomMixerStreamActions.roomMixerStreamGetSuccess({
          stream: new RoomMixerStreamEntity(_)
        })
      ),
      catchError((error) => [
        LokiActions.log({
          args: [
            error === STREAM_MISSING
              ? "Room mixer stream missing after started (contract violation, or no WebRTC destination)"
              : "Room mixer stream get failed",
            {
              roomId,
              error: (error as null | { message?: unknown })?.message,
              code: errorCode(error),
              status: apiErrorHttpStatus(error),
              roomContextId: this.roomContextFacade.roomContextId
            }
          ]
        }),
        RoomMixerStreamActions.roomMixerStreamGetFailure()
      ])
    );
  }

  // Stops the retries when the live of the room stops or fails (ERROR), the
  // stream is disposed (viewer left) or reset (socket reconnected: the join
  // replays the live status).
  private roomMixerStreamGetCancel(roomId: RoomId) {
    return merge(
      this.actions$.pipe(
        ofType(RoomLiveStreamStatusEvents.update),
        filter((_) => endsStream(_.status) && _.roomId === roomId)
      ),
      this.actions$.pipe(
        ofType(RoomMixerStreamActions.dispose, RoomMixerStreamActions.reset)
      )
    );
  }

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomMixerStreamActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomMixerStreamActions.destroy)))
        )
      )
    );
  }
}
